export const FORECLOSURE_CASE_NUMBER_QUESTION_TEXT = "Foreclosure Case Number";

/** Alphanumeric-only, casefolded tokens treated as an unanswered case number. */
const FORECLOSURE_CASE_NUMBER_NONE_TOKENS = new Set([
  "na",
  "none",
  "null",
  "nil",
  "unk",
  "unkn",
  "unknown",
  "tbd",
  "tba",
  "tbc",
  "pending",
  "missing",
  "unspecified",
  "notapplicable",
  "notavailable",
  "notknown",
  "notprovided",
  "noneprovided",
  "nonegiven",
  "nonespecified",
  "notspecified",
  "unavailable",
  "undetermined",
  "nocase",
  "nocasenumber",
  "nothing",
  "empty",
  "blank",
  "xxx",
  "xxxx",
  "nk",
  "notfound",
]);

function foreclosureCaseNumberToken(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

export function effectiveForeclosureCaseNumber(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  const text = typeof value === "string" ? value.trim() : String(value).trim();
  if (!text) {
    return null;
  }
  const token = foreclosureCaseNumberToken(text);
  if (!token || FORECLOSURE_CASE_NUMBER_NONE_TOKENS.has(token)) {
    return null;
  }
  return text;
}

export function getForeclosureCaseNumberAnswer(
  questions:
    | Array<{
        id: string;
        question: string;
      }>
    | undefined,
  answers: Record<string, unknown> | undefined,
): string | null {
  const question = questions?.find(
    (item) =>
      item.question.trim().toLowerCase() ===
      FORECLOSURE_CASE_NUMBER_QUESTION_TEXT.toLowerCase(),
  );
  if (!question) {
    return null;
  }

  const rawAnswer = answers?.[question.id];
  return effectiveForeclosureCaseNumber(rawAnswer);
}

export function getForeclosureCaseLiveLinkConfig(
  questions:
    | Array<{
        id: string;
        question: string;
        url_template?: string | null;
        copy_value_to_clipboard?: boolean;
        http_method?: string | null;
        value_extract_regex?: string | null;
        request_fields?: Record<string, string> | null;
        browser_helper_config?: Record<string, unknown> | null;
        blank_answer_url?: string | null;
      }>
    | undefined,
  answers: Record<string, unknown> | undefined,
): {
  caseNumber: string | null;
  urlTemplate: string | null;
  copyToClipboard: boolean;
  httpMethod: string;
  valueExtractRegex: string | null;
  requestFields: Record<string, string> | null;
  browserHelperConfig: Record<string, unknown> | null;
  blankAnswerUrl: string | null;
} {
  const question = questions?.find(
    (item) =>
      item.question.trim().toLowerCase() ===
      FORECLOSURE_CASE_NUMBER_QUESTION_TEXT.toLowerCase(),
  );
  if (!question) {
    return {
      caseNumber: null,
      urlTemplate: null,
      copyToClipboard: false,
      httpMethod: "GET",
      valueExtractRegex: null,
      requestFields: null,
      browserHelperConfig: null,
      blankAnswerUrl: null,
    };
  }

  return {
    caseNumber: getForeclosureCaseNumberAnswer(questions, answers),
    urlTemplate: question.url_template ?? null,
    copyToClipboard: question.copy_value_to_clipboard ?? false,
    httpMethod: question.http_method ?? "GET",
    valueExtractRegex: question.value_extract_regex ?? null,
    requestFields: question.request_fields ?? null,
    browserHelperConfig: question.browser_helper_config ?? null,
    blankAnswerUrl: question.blank_answer_url ?? null,
  };
}

export function formatAttomFieldLabel(key: string): string {
  return key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatAttomFieldValue(value: unknown): string {
  if (value == null || value === "") {
    return "—";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

export type AttomCaseSummaryEntry = [string, string | number | null];

/** Case summary fields whose key ends with ``_date`` (e.g. start_date, closed_date). */
export function isAttomCaseSummaryDateField(key: string): boolean {
  return key.endsWith("_date");
}

/** Order case summary with case_number first when present. */
export function caseSummaryEntries(
  caseData: Record<string, string | number | null>,
): AttomCaseSummaryEntry[] {
  const entries = Object.entries(caseData);
  const caseNumberEntry = entries.find(([key]) => key === "case_number");
  if (!caseNumberEntry) {
    return entries;
  }
  return [caseNumberEntry, ...entries.filter(([key]) => key !== "case_number")];
}

/** APN + address first in the leftmost (property) column. */
export const ATTOM_CASE_SUMMARY_LEFT_PRIORITY_KEYS = [
  "apn",
  "property_address_full",
] as const;

/** Lender / borrower first in the middle (case parties) column. */
export const ATTOM_CASE_SUMMARY_MIDDLE_TOP_KEYS = [
  "latest_lender_name",
  "latest_borrower_name_owner",
] as const;

export function isAttomCaseSummaryPropertyField(key: string): boolean {
  return (
    key === "apn" ||
    key.startsWith("property_") ||
    key.startsWith("situs_") ||
    key === "tax_property_jurisdiction_name"
  );
}

export function isAttomCaseSummaryMiddleTopField(key: string): boolean {
  return (ATTOM_CASE_SUMMARY_MIDDLE_TOP_KEYS as readonly string[]).includes(
    key,
  );
}

/**
 * Split summary fields into:
 * - left: APN, full address, and other property details
 * - middle: latest lender / borrower at top, then remaining case fields
 * - date: ``*_date`` fields (right column)
 */
export function partitionCaseSummaryEntries(
  caseData: Record<string, string | number | null>,
): {
  left: AttomCaseSummaryEntry[];
  middle: AttomCaseSummaryEntry[];
  date: AttomCaseSummaryEntry[];
} {
  const leftByKey = new Map<string, AttomCaseSummaryEntry>();
  const middleTopByKey = new Map<string, AttomCaseSummaryEntry>();
  const middleRest: AttomCaseSummaryEntry[] = [];
  const date: AttomCaseSummaryEntry[] = [];

  for (const entry of caseSummaryEntries(caseData)) {
    const [key] = entry;
    if (isAttomCaseSummaryDateField(key)) {
      date.push(entry);
    } else if (isAttomCaseSummaryPropertyField(key)) {
      leftByKey.set(key, entry);
    } else if (isAttomCaseSummaryMiddleTopField(key)) {
      middleTopByKey.set(key, entry);
    } else {
      middleRest.push(entry);
    }
  }

  const leftPriority = ATTOM_CASE_SUMMARY_LEFT_PRIORITY_KEYS.flatMap((key) => {
    const entry = leftByKey.get(key);
    return entry ? [entry] : [];
  });
  const leftPrioritySet = new Set(
    ATTOM_CASE_SUMMARY_LEFT_PRIORITY_KEYS as readonly string[],
  );
  const leftRest = [...leftByKey.entries()]
    .filter(([key]) => !leftPrioritySet.has(key))
    .map(([, entry]) => entry);
  const left = [...leftPriority, ...leftRest];

  const middleTop = ATTOM_CASE_SUMMARY_MIDDLE_TOP_KEYS.flatMap((key) => {
    const entry = middleTopByKey.get(key);
    return entry ? [entry] : [];
  });
  const middle = [...middleTop, ...middleRest];

  return { left, middle, date };
}
